import React, { useEffect, useMemo, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { trackPageView } from '../../lib/gaEvents';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { SITE_URL } from '../../utils/env';
import { useUserStore } from '../../stores/userStore';
import useTrackPageView from '../../hooks/useTrackPageView';
import theme from '../../theme';
import InFeedAd from '../../components/ad/InFeedAd';
import NoData from '../../components/common/NoData';
import PageLayout from '../../components/layout/PageLayout';
import PageTitle from '../../components/common/PageTitle';
import Pagination from '../../components/common/Pagination';
import ProgressBar from '../../components/common/ProgressBar';
import SelectFilter from '../../components/common/SelectFilter';
import SEO from '../../components/common/SEO';
import ReviewCard from '../../components/card/ReviewCard';
import SubTab from '../../components/button/SubTab';
import TabButton from '../../components/button/TabButton';
import { useReviewsAlbums } from './hooks/useReviewsAlbums';

const REGION_TABS = [
  { label: '전체', key: 'all' },
  { label: '국내', key: 'korean' },
  { label: '해외', key: 'global' },
];

const CATEGORY_TABS = [
  { label: '최근 리뷰', key: 'recent' },
  { label: '인기 리뷰', key: 'popular' },
];

const TYPE_FILTER_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '앨범', value: 'albums' },
  { label: '싱글', value: 'singles' },
];

const PERIOD_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '일주일', value: 'week' },
  { label: '한달', value: 'month' },
];

const SORT_OPTIONS_BY_CATEGORY = {
  recent: [
    { label: '최신순', value: 'latest' },
    { label: '댓글순', value: 'comments' },
    { label: '평점순', value: 'rating' },
  ],
  popular: [
    { label: '인기순', value: 'likes' },
    { label: '댓글순', value: 'comments' },
    { label: '평점순', value: 'rating' },
  ],
};

const getDefaultPeriod = (category) => (category === 'recent' ? 'month' : 'all');
const normalizePeriod = (value, category = 'recent') =>
  value === 'today' ? getDefaultPeriod(category) : value || getDefaultPeriod(category);

const ITEMS_PER_AD = { xs: 5, md: 20, lg: 25 };
const ITEMS_PER_PAGE = { xs: 10, md: 40, lg: 50 };

const getItemsPerPage = (isXs, isMd) =>
  isXs ? ITEMS_PER_PAGE.xs : isMd ? ITEMS_PER_PAGE.md : ITEMS_PER_PAGE.lg;

const getItemsPerAd = (isXs, isMd) =>
  isXs ? ITEMS_PER_AD.xs : isMd ? ITEMS_PER_AD.md : ITEMS_PER_AD.lg;

const toReviewsCategory = (category, region = 'all') =>
  `${region}_${category === 'popular' ? 'popular_reviews' : 'recent_reviews'}`;

const getDefaultSort = (category) => (category === 'popular' ? 'likes' : 'latest');
const normalizeSort = (category, value) => {
  const allowedValues = SORT_OPTIONS_BY_CATEGORY[category].map((option) => option.value);
  return allowedValues.includes(value) ? value : getDefaultSort(category);
};

const Reviews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [regionTab, setRegionTab] = useState('all');
  const [categoryTab, setCategoryTab] = useState('recent');
  const [subTab, setSubTab] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [period, setPeriod] = useState('month');
  const { user } = useUserStore();

  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const itemsPerAd = getItemsPerAd(isXs, isMd);

  useTrackPageView({
    pageType: PAGE_TYPE.REVIEWS_MAIN,
    reviewsCategory: toReviewsCategory(categoryTab, regionTab),
  });

  const REGION_TAB_ITEMS = useMemo(
    () => REGION_TABS.map((tab) => ({ id: tab.key, label: tab.label })),
    [],
  );
  const CATEGORY_TAB_ITEMS = useMemo(
    () => CATEGORY_TABS.map((tab) => ({ id: tab.key, label: tab.label })),
    [],
  );

  const legacyCategory = searchParams.get('category');
  const queryRegion =
    searchParams.get('region') ||
    (legacyCategory === 'global' ? 'global' : legacyCategory === 'korean' ? 'korean' : 'all');
  const queryCategory =
    legacyCategory === 'korean' || legacyCategory === 'global'
      ? 'recent'
      : legacyCategory === 'popular'
        ? 'popular'
        : 'recent';
  const queryType = searchParams.get('type') || 'all';
  const querySort = normalizeSort(queryCategory, searchParams.get('sort'));
  const queryPeriod = normalizePeriod(searchParams.get('period'), queryCategory);
  const queryPage = parseInt(searchParams.get('page')) || 1;
  const querySize = getItemsPerPage(isXs, isMd);
  const apiRegion = queryRegion === 'all' ? null : queryRegion === 'global' ? 'global' : 'kr';

  const { data: reviewsData, isPending: loading } = useReviewsAlbums({
    region: apiRegion,
    category: queryCategory,
    type: queryType,
    sort: querySort,
    page: queryPage,
    size: querySize,
    period: queryPeriod,
  });

  const reviews = reviewsData?.albums ?? [];
  const pagination = reviewsData?.pagination ?? { currentPage: 1, totalPages: 1, totalItems: 0 };

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const region =
      searchParams.get('region') ||
      (categoryParam === 'global' ? 'global' : categoryParam === 'korean' ? 'korean' : 'all');
    const category =
      categoryParam === 'korean' || categoryParam === 'global'
        ? 'recent'
        : categoryParam === 'popular'
          ? 'popular'
          : 'recent';
    const type = searchParams.get('type') || 'all';
    const sort = normalizeSort(category, searchParams.get('sort'));
    const periodParam = normalizePeriod(searchParams.get('period'), category);

    setRegionTab(region);
    setCategoryTab(category);
    setSubTab(type);
    setSortBy(sort);
    setPeriod(periodParam);
  }, [searchParams]);

  const buildParams = ({
    region = regionTab,
    category = categoryTab,
    type = subTab,
    sort,
    page = '1',
    periodValue = period || getDefaultPeriod(category),
  }) => ({
    region,
    category,
    type,
    sort: normalizeSort(category, sort),
    period: periodValue,
    page,
  });

  const handleRegionChange = (newRegion) => {
    trackPageView(
      {
        pageType: PAGE_TYPE.REVIEWS_MAIN,
        reviewsCategory: toReviewsCategory(categoryTab, newRegion),
      },
      user,
    );
    setSearchParams(buildParams({ region: newRegion, category: categoryTab, sort: sortBy }));
  };

  const handleCategoryTabChange = (newCategory) => {
    const nextSort = getDefaultSort(newCategory);
    const nextPeriod = getDefaultPeriod(newCategory);
    trackPageView(
      { pageType: PAGE_TYPE.REVIEWS_MAIN, reviewsCategory: toReviewsCategory(newCategory) },
      user,
    );
    setSearchParams(
      buildParams({ category: newCategory, type: subTab, sort: nextSort, periodValue: nextPeriod }),
    );
  };

  const handleSubTabChange = (nextSubTab) => {
    setSearchParams(buildParams({ type: nextSubTab, sort: sortBy }));
  };

  const handleSortChange = (newSort) => {
    setSearchParams(buildParams({ sort: newSort }));
  };

  const handlePeriodChange = (newPeriod) => {
    setSearchParams(buildParams({ sort: sortBy, periodValue: newPeriod }));
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams(buildParams({ sort: sortBy, page: newPage.toString() }));
  };

  return (
    <PageLayout>
      <SEO title="Reviews" description={SEO_DESCRIPTIONS.REVIEWS} url={`${SITE_URL}/reviews`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Reviews' },
            ],
          })}
        </script>
      </Helmet>
      {reviews.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: reviews
                .filter((item) => item.albumImageUrl)
                .map((item, index) => ({
                  '@type': 'ListItem',
                  name: item.albumTitle,
                  image: item.albumImageUrl,
                  url: item.albumSlug
                    ? `${SITE_URL}/reviews/${item.albumSlug}/user-reviews/${item.reviewId}`
                    : `${SITE_URL}/reviews/local/${item.albumId}/user-reviews/${item.reviewId}`,
                  position: index + 1,
                })),
            })}
          </script>
        </Helmet>
      )}

      <PageTitle textAlign={{ xs: 'left', md: 'center' }} mb={{ xs: 2, md: 3.75, lg: 5 }}>
        리뷰
      </PageTitle>

      <TabButton
        tabs={CATEGORY_TAB_ITEMS}
        activeTab={categoryTab}
        onTabChange={handleCategoryTabChange}
        marginTop={0}
        centered={!isXs}
        sx={{
          '& .MuiTabs-flexContainer': {
            gap: { xs: 1, md: 1.5 },
          },
          '& .MuiTab-root': {
            minWidth: { xs: 136, md: 190 },
            py: { xs: 1.5, md: 1.75 },
            fontSize: { xs: 14, md: 18 },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 2, md: 3 },
            mt: { xs: 2.5, md: 3.75 },
          }}
        >
          <SubTab
            tabs={REGION_TAB_ITEMS}
            activeTab={regionTab}
            onTabChange={handleRegionChange}
            sx={{
              flexShrink: 0,
              gap: { xs: 1.5, lg: 2 },
              maxWidth: '100%',
              '& > div': {
                px: { xs: 0.25, md: 0.5 },
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              flexShrink: 0,
              justifyContent: 'flex-end',
              gap: { xs: 1.5, md: 2 },
              rowGap: { xs: 1, md: 1.5 },
              transform: { xs: 'translateY(-5px)', md: 'translateY(-1px)' },
            }}
          >
            <SelectFilter
              label="유형"
              value={subTab}
              onChange={handleSubTabChange}
              options={TYPE_FILTER_OPTIONS}
              hideLabelOnMobile
              valueIconGap={{ xs: 0.25, md: 0.5 }}
            />
            <SelectFilter
              label="기간"
              value={period}
              onChange={handlePeriodChange}
              options={PERIOD_OPTIONS}
              hideLabelOnMobile
              valueIconGap={{ xs: 0.25, md: 0.5 }}
            />
            <SelectFilter
              label="정렬"
              value={sortBy}
              onChange={handleSortChange}
              options={SORT_OPTIONS_BY_CATEGORY[categoryTab]}
              hideLabelOnMobile
              valueIconGap={{ xs: 0.25, md: 0.5 }}
            />
          </Box>
        </Box>

        <ProgressBar isLoading={loading}>
          {reviews.length === 0 ? (
            <NoData />
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(4, minmax(0, 1fr))',
                    lg: 'repeat(5, minmax(0, 1fr))',
                  },
                  columnGap: { xs: 2, lg: 2.5 },
                  rowGap: { xs: 3, md: 5 },
                  mt: { xs: 2, md: 2.5, lg: 3.75 },
                }}
              >
                {reviews.flatMap((item, index) => {
                  const card = (
                    <Box key={`${item.reviewId}-${index}`} sx={{ minWidth: 0 }}>
                      <ReviewCard
                        review={item}
                        variant="horizontalMobile"
                        referrerSection={toReviewsCategory(categoryTab, regionTab)}
                      />
                    </Box>
                  );

                  if ((index + 1) % itemsPerAd === 0 && index < reviews.length - 1) {
                    return [
                      card,
                      <Box key={`ad-${index}`} sx={{ gridColumn: '1 / -1' }}>
                        <InFeedAd pageType={PAGE_TYPE.REVIEWS_MAIN} />
                      </Box>,
                    ];
                  }
                  return card;
                })}
              </Box>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
              />
            </>
          )}
        </ProgressBar>
      </TabButton>
    </PageLayout>
  );
};

export default Reviews;
